import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaCalendarAlt, FaNewspaper } from 'react-icons/fa';

const NewsItem = () => {
  const { slug } = useParams();
  const [newsItem, setNewItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const newsRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (newsRef.current) observer.observe(newsRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchNewsItem = async () => {
      const cacheKey = `newsItem-${slug}`;
      const cacheTimestampKey = `newsItemTimestamp-${slug}`;
  
      try {
        const cachedData = localStorage.getItem(cacheKey);
        const cachedTimestamp = localStorage.getItem(cacheTimestampKey);
  
        const isCacheValid = cachedTimestamp && (new Date().getTime() - new Date(parseInt(cachedTimestamp)).getTime()) < 12 * 3600 * 1000;
  
        if (cachedData && isCacheValid) {
          setNewItem(JSON.parse(cachedData));
        } else {
          const response = await fetch(`https://hub.mileageriot.com/api/news/${slug}`);
          const data = await response.json();
          setNewItem(data.data);
          localStorage.setItem(cacheKey, JSON.stringify(data.data));
          localStorage.setItem(cacheTimestampKey, new Date().getTime().toString());
        }
      } catch (error) {
        console.error('Error fetching news item data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchNewsItem();
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('ru', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <section className="news-item-modern page-news-item" ref={newsRef}>
        <div className="container">
          <div className="text-center">
            <div className="loading-spinner">
              <div className="spinner-modern"></div>
            </div>
            <p className="mt-3 text-white-50">{t('news.loading') || 'Загружаем новость...'}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!newsItem) {
    return (
      <section className="news-item-modern page-news-item" ref={newsRef}>
        <div className="container">
          <div className="text-center">
            <h3 className='text-white'>{t('news.error') || 'Ошибка загрузки новости'}</h3>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="news-item-modern page-news-item" ref={newsRef}>
      <div className="container">
        {/* Header */}
        <div className="news-item-header fade-in-up stagger-1">
          <Link to="/news" className="btn btn-back">
            <FaArrowLeft className="me-2" />
            {t('news.backToNews') || 'Назад к новостям'}
          </Link>
          
          <div className="section-badge mt-4">
            <FaNewspaper className="me-2" />
            <span>{t('news.badge') || 'Новости'}</span>
          </div>
        </div>

        {/* News Content */}
        <div className="news-item-content fade-in-up stagger-2">
          <div className="news-item-card glass-container">
            {/* Image */}
            <div className="news-item-image-container">
              <img 
                className="news-item-image" 
                src={newsItem.image || 'https://hub.mileageriot.com/assets/images/banner/banner.jpg'}  
                alt={newsItem.title || 'News Image'}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
                }}
              />
              <div className="news-item-overlay">
                <div className="news-item-meta">
                  <span className="news-item-date">
                    <FaCalendarAlt className="me-2" />
                    {formatDate(newsItem.created_at)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="news-item-body">
              <h1 className="news-item-title text-white">{newsItem.title}</h1>
              <div className="news-item-divider"></div>
              
              <div className="news-item-text text-white" 
                   dangerouslySetInnerHTML={{ __html: newsItem.content }} 
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="news-item-bg-decoration"></div>
    </section>
  );
};

export default NewsItem;